import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BoardResponse, CardView, PatchCard, Stage } from "@breeze/shared";
import { fetchBoard, patchCard } from "../api.js";
import { getSessionId } from "../session.js";
import { applyCardToBoardCache } from "../cache.js";

export function useBoard(
  includeArchived: boolean
): ReturnType<typeof useQuery<BoardResponse>> {
  return useQuery<BoardResponse>({
    queryKey: ["board", { archived: includeArchived }],
    queryFn: () => fetchBoard(includeArchived),
  });
}

type MoveArgs = {
  cardId: number;
  stage: Stage;
  position: number;
  closedReason?: string;
};

export function usePatchCard(): ReturnType<
  typeof useMutation<{ card: CardView }, Error, MoveArgs>
> {
  const qc = useQueryClient();
  return useMutation<{ card: CardView }, Error, MoveArgs>({
    mutationFn: async ({ cardId, stage, position, closedReason }) => {
      const body: PatchCard = {
        stage,
        position,
        originatorSessionId: getSessionId(),
        ...(closedReason !== undefined ? { closedReason } : {}),
      };
      return patchCard(cardId, body);
    },
    onMutate: async ({ cardId, stage, position, closedReason }) => {
      await qc.cancelQueries({ queryKey: ["board"] });
      qc.getQueryCache()
        .findAll({ queryKey: ["board"] })
        .forEach((query) => {
          const prev = query.state.data as BoardResponse | undefined;
          if (!prev) return;
          const cards = prev.cards.map((c) =>
            c.cardId === cardId
              ? {
                  ...c,
                  stage,
                  position,
                  closedReason: closedReason ?? c.closedReason,
                }
              : c
          );
          qc.setQueryData(query.queryKey, { ...prev, cards });
        });
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: ["board"] });
    },
    onSettled: () => {
      // Real server-truth lands via SSE; no refetch needed.
    },
  });
}

// Generic card PATCH (priority / assignee / archive) — full server response
// replaces the matching card in every cached board variant.
export function useUpdateCard(): ReturnType<
  typeof useMutation<
    { card: CardView },
    Error,
    { cardId: number; patch: PatchCard }
  >
> {
  const qc = useQueryClient();
  return useMutation<
    { card: CardView },
    Error,
    { cardId: number; patch: PatchCard }
  >({
    mutationFn: ({ cardId, patch }) =>
      patchCard(cardId, {
        ...patch,
        originatorSessionId: getSessionId(),
      }),
    onSuccess: ({ card }) => applyCardToBoardCache(qc, card),
  });
}
